import { useQuery } from '@tanstack/react-query';

import { Section } from '@/components/layout/Section';
import type { UserRole } from '@/lib/api/domains/users';
import { accountPopulationReportQuery } from '../api/report-queries';

/*
 * The Administrator-gated account-population report surface (F02 R39). Shows,
 * for ONE selected organization, the account population counted three ways —
 * by role, by the is_administrator privilege, and by dormancy — read from
 * `accountPopulationReportQuery` (GET /reports/access/account-population).
 *
 *   - AC-56a2a741 — how many accounts hold each role (a per-role count): the
 *     `report-by-role` region, a labelled row per role with its count.
 *   - AC-79a95f73 — how many hold the administrator privilege, SEPARATELY from
 *     the role counts (they are different things): the `report-privilege-count`
 *     region.
 *   - AC-05c223d0 — how many are dormant (not signed in within the period): the
 *     `report-dormant-count` region.
 *   - AC-832c0d75 — the figures are scoped to the ONE selected organization,
 *     addressed by its opaque id; switching org reads a different query key and
 *     shows that org's figures, never all orgs mingled. The query is keyed by
 *     `organizationId`, so the per-org separation is the cache's.
 *   - AC-02d2e999 (consumed) — the server gates the route on the privilege and
 *     refuses anyone else (403); this surface is the courtesy half (the way in
 *     is withheld by the route guard, and the server is the backstop).
 *
 * PII: the report is COUNTS ONLY. This surface renders aggregate numbers and
 * the org's opaque id — never a person's name, email or account id (product
 * rule: PII/PHI never leaves in reports/telemetry/logs). The server returns
 * aggregates alone; there is no account list to render.
 */

const ROLE_LABELS: Record<UserRole, string> = {
  tpabilling: 'TPA Billing',
  client: 'Client',
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role as UserRole] ?? role;
}

export interface AccountPopulationReportProps {
  /**
   * The opaque id of the organization to read the report FOR (Kelly's own or a
   * named other org — AC-832c0d75). Never a name: the surface is addressed by
   * id so no PII travels (AGENTS.md: ids only).
   */
  organizationId: string;
}

export function AccountPopulationReport({
  organizationId,
}: AccountPopulationReportProps): React.ReactElement {
  // Keyed by the organization id, so Kelly's own org and a named other org are
  // cached and read SEPARATELY (AC-832c0d75) — switching org reads a different
  // key, never a mingled figure.
  const { data, isLoading, isError } = useQuery(
    accountPopulationReportQuery(organizationId),
  );

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading the account population…
      </p>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        The account population could not be loaded.
      </p>
    );
  }

  // The by-role breakdown renders only the roles the server reported a count
  // for; a role with no accounts in this org is absent, so a different org's
  // roles never mingle in (AC-832c0d75).
  const byRole = Object.entries(data.byRole).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number',
  );

  return (
    <Section aria-label="Account population" className="gap-6">
      {/* AC-56a2a741 — how many accounts hold each role. */}
      <section data-testid="report-by-role" className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Accounts by role</h3>
        <dl className="flex flex-col gap-1">
          {byRole.map(([role, count]) => (
            <div key={role} className="flex items-center justify-between gap-4">
              <dt className="text-sm text-muted-foreground">
                {roleLabel(role)}
              </dt>
              <dd className="text-sm font-medium tabular-nums">{count}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* AC-79a95f73 — how many hold the administrator privilege, counted
          separately from the role counts (they are different things). */}
      <section
        data-testid="report-privilege-count"
        className="flex items-center justify-between gap-4"
      >
        <h3 className="text-sm font-medium">
          Hold the administrator privilege
        </h3>
        <p className="text-sm font-medium tabular-nums">
          {data.administratorPrivilegeCount}
        </p>
      </section>

      {/* AC-05c223d0 — how many are dormant (not signed in within the period). */}
      <section
        data-testid="report-dormant-count"
        className="flex items-center justify-between gap-4"
      >
        <h3 className="text-sm font-medium">Dormant accounts</h3>
        <p className="text-sm font-medium tabular-nums">{data.dormantCount}</p>
      </section>
    </Section>
  );
}
